import React from 'react';

import { getDocument } from '@atlaskit/browser-apis';
import { Root } from '@atlaskit/navigation-system/layout/root';
// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import { SpotlightManager } from '@atlaskit/onboarding';
import { usePanelContext } from '@atlassian/entry-point-panel/use-panel-context';

import { DISMISS_SPOTLIGHT_TOUR_EVENT_NAME } from '@townsquare/config/constants';
import { GoalCapabilitiesProvider } from '@townsquare/goal-capabilities';
import { GriffinLayout } from '@townsquare/griffin-layout';
import { GriffinTopNavigation } from '@townsquare/griffin-navigation';
import { GlobalAppShortcutsProvider } from '@townsquare/griffin-navigation/global-navigation-context';
import { AppHomeWrapper } from '@townsquare/navigation/app-home-wrapper';
import { IntroPopup } from '@townsquare/onboarding/intro-popup';
import RovoAppificationChangeboarding from '@townsquare/post-office/message-components/rovo-appification-modal';
import { TownsquareGlobalPlacement } from '@townsquare/post-office/placements';
import { ProjectCapabilitiesProvider } from '@townsquare/project-capabilities';
import { useRovoAppification } from '@townsquare/rovo/appification';
import { SIDEBAR_PANEL } from '@townsquare/sidebar-panel/panels';
import { usePreloadedEntryPointPanel } from '@townsquare/sidebar-panel-utils/hooks/use-preloaded-entry-point-panel';
import { SiteBannerProvider } from '@townsquare/site-banner';
import { FullScreenSpaLoader } from '@townsquare/spa-loader';
import { expVal } from '@townsquare/stat-sig';
import { NAVX_UC_ML_POWERED_RELEVANCY_ENGINE } from '@townsquare/stat-sig/experiments';
import { fg } from '@townsquare/stat-sig/gate';
import { useNoWorkspaceExperience } from '@townsquare/workspace-store';

import { ProductHome } from '../navigation/ProductHome';
import { useUniversalCreateItems } from '../navigation/use-universal-create-items';

import { ConditionalSideNav, useDisableRovoSearchSideNav } from './ConditionalSideNav';

const document = getDocument();

type Props = {
	/**
	 * Prop to disable customization of the Home sidebar, for testing purposes.
	 */
	sidebarCustomizationDisabled?: boolean;
};

export function GriffinRoute(props: Props): React.JSX.Element {
	const { rovoAppificationState } = useRovoAppification();

	if (rovoAppificationState === 'loading') {
		return <FullScreenSpaLoader />;
	}

	return (
		<Root isSideNavShortcutEnabled={fg('navx-full-height-sidebar')}>
			<SiteBannerProvider>
				<GlobalAppShortcutsProvider>
					<GriffinRouteComponent {...props} />
				</GlobalAppShortcutsProvider>
			</SiteBannerProvider>
		</Root>
	);
}

const onBlanketClicked = () => {
	document?.dispatchEvent(new Event(DISMISS_SPOTLIGHT_TOUR_EVENT_NAME));
};

function GriffinRouteComponent({ sidebarCustomizationDisabled }: Props) {
	const noWorkspaceExperience = useNoWorkspaceExperience();
	const preloadedPanel = usePreloadedEntryPointPanel();
	const { Panel: SidebarPanel, Provider: SidebarPanelContext } = usePanelContext(
		SIDEBAR_PANEL,
		preloadedPanel,
	);
	const disableSideNavRovoSearch = useDisableRovoSearchSideNav();

	return (
		<>
			<SpotlightManager onBlanketClicked={onBlanketClicked} blanketIsTinted={false}>
				<TownsquareGlobalPlacement />
				<RovoAppificationChangeboarding />
				<SidebarPanelContext>
					<GoalCapabilitiesProvider>
						<ProjectCapabilitiesProvider>
							<GriffinTopSection hideSideNav={disableSideNavRovoSearch} />
						</ProjectCapabilitiesProvider>
					</GoalCapabilitiesProvider>
					<ConditionalSideNav customizationDisabled={sidebarCustomizationDisabled} />
					<GriffinLayout PlatformSidebarPanel={SidebarPanel} />
				</SidebarPanelContext>
			</SpotlightManager>
			{noWorkspaceExperience && <IntroPopup />}
		</>
	);
}

const GriffinTopSection = ({ hideSideNav }: { hideSideNav?: boolean }) => {
	const {
		items: universalCreateItems,
		compact,
		isItemLoading,
		foldable,
	} = useUniversalCreateItems();

	return (
		<GriffinTopNavigation
			renderProductHome={() => (
				<AppHomeWrapper>
					<ProductHome />
				</AppHomeWrapper>
			)}
			universalCreateItems={universalCreateItems}
			compact={compact}
			enableCrossAppCreation={
				expVal(NAVX_UC_ML_POWERED_RELEVANCY_ENGINE, 'isEnabled', false)
					? 'personalizeWithGrowthRecommendations'
					: 'personalize'
			}
			hideSideNavToggleButton={hideSideNav}
			isItemLoading={isItemLoading}
			foldable={foldable}
		/>
	);
};
